import createStorage, { sessionStorage, Storage } from "./Storage.ts";
import { ensureSessionInfoKeys } from "./utils.ts";
import {
  APP_VERSION_KEY,
  KLARITICS_CONFIG_KEY,
  KLARITICS_INSTALL_KEY,
  KLARITICS_LAST_EVENT_TIME,
  KLARITICS_UNSENT_CHUNK_DETAILS,
  KLARITICS_USER_ID,
  KLARITICS_CURRENT_CHUNK,
  DEFAULT_CONFIG,
  OLD_SESSIONS_DATA,
  KLARITICS_LT_COUNT,
  KLARITICS_SESSION_CHUNK_NUMBER_MAP,
  KLARITICS_RETAINED_DAYS,
  KLARITICS_USER_ATTR_KEY,
  LOCAL_STORAGE,
  SESSION_STORAGE,
  KLARITICS_CLIENT_EVENTS_KEY
} from "./constants.ts";
import {
  decode,
  encode,
  getJSONOrNull,
  isDefined,
  isNotDefined,
  millisToSeconds
} from "./utils.ts";
import { chunkInitialState, DEFAULT_CHUNK_DETAILS } from "./models.ts";
import Logger from "./Logger.ts";
import Cookie from "./Cookie.ts";
import type { Chunk, Details, ChunkDetails } from "./types/types.ts";

export default class DataManager {
  private _siteId: string = "";
  storage: Storage;
  sessionStorage: Storage;

  constructor(siteId: string | undefined) {
    this._siteId = siteId || "";
    this.storage = createStorage();
    this.sessionStorage = sessionStorage();
    Logger.debug("DataManager : Initialized");
  }

  getltCount(name: string): number {
    try {
      const data = this.storage.get(KLARITICS_LT_COUNT);
      if (data) {
        const decoded = decode(this._siteId, data);
        const parsed = JSON.parse(decoded) as Record<string, number>;
        const evName = name.replace("'", "").replace("'", "");
        return parsed[evName];
      }
    } catch (e) {
      Logger.error(
        "Datamanager : Error getting the LT Count for the event -" + e
      );
    }
    return 0;
  }

  getExistingConfig = (): Details => {
    let config = this.storage.get(KLARITICS_CONFIG_KEY);
    if (isDefined(config)) {
      try {
        config = JSON.parse(decode(this._siteId, config!)) as unknown as string;
      } catch (e) {
        config = null;
      }
    }
    return (config as unknown as Details) ?? DEFAULT_CONFIG;
  };

  persistConfig = (config: Details | undefined): void => {
    this.storage.set(
      KLARITICS_CONFIG_KEY,
      encode(this._siteId, JSON.stringify(config)) as string
    );
  };

  persistClientEventsConfig = (config: unknown): void => {
    this.storage.set(
      KLARITICS_CLIENT_EVENTS_KEY,
      encode(this._siteId, JSON.stringify(config)) as string
    );
  };

  getClientEventsConfig = (): Record<string, unknown> => {
    let config: unknown = this.storage.get(KLARITICS_CLIENT_EVENTS_KEY);
    if (isDefined(config)) {
      try {
        config = JSON.parse(decode(this._siteId, config as string));
      } catch (e) {
        config = null;
      }
    }
    return (config as Record<string, unknown>) ?? {};
  };

  persistUserId = (id: string): void => {
    this.storage.set(KLARITICS_USER_ID, id);
  };

  getUserId = (): string | null => {
    try {
      return this.storage.get(KLARITICS_USER_ID);
    } catch (e) {
      Logger.debug("Datamanager : Can not get user id from storage - " + e);
    }
    return null;
  };

  persistAppVersion = (version: string): void =>
    this.storage.set(APP_VERSION_KEY, version);

  getAppVersion = (): string | null => {
    const appVersion = this.storage.get(APP_VERSION_KEY);
    if (!isDefined(appVersion) && Cookie.isCookieAvailable()) {
      const storedAppVersion = Cookie.get(APP_VERSION_KEY);
      if (isDefined(storedAppVersion)) {
        this.storage.set(APP_VERSION_KEY, storedAppVersion!);
        return storedAppVersion;
      }
    }
    return appVersion;
  };

  persist = (
    key: string,
    value: string,
    storage: string = LOCAL_STORAGE
  ): void => {
    if (storage === LOCAL_STORAGE) {
      this.storage.set(key, value);
    } else if (storage === SESSION_STORAGE) {
      this.sessionStorage.set(key, value);
    }
  };

  getItem = (
    key: string,
    encoded = false,
    storage: string = LOCAL_STORAGE
  ): unknown => {
    let data: string | null;
    if (storage === LOCAL_STORAGE) {
      data = this.storage.get(key);
    } else {
      data = this.sessionStorage.get(key);
    }

    if (encoded) {
      const jsonData = getJSONOrNull(data);
      if (jsonData === null) {
        try {
          return JSON.parse(decode(this._siteId, data as string));
        } catch (e) {
          Logger.debug(`Datamanager : ${key} - value not found in storage`);
          return null;
        }
      } else {
        return jsonData;
      }
    }
    Logger.debug(
      `Datamanager : ${key}'s value from storage is ${JSON.stringify(data)}`
    );
    return data;
  };

  getItemOrDefault = (
    key: string,
    defaultValue: unknown,
    encoded = false,
    storage: string = LOCAL_STORAGE
  ): unknown => {
    const value = this.getItem(key, encoded, storage);
    if (isDefined(value)) return value;
    Logger.debug(
      `Datamanager : ${key}'s value is not defined. Returning default-${JSON.stringify(
        defaultValue
      )}`
    );
    return defaultValue;
  };

  clearStorage = (): void => {
    this.storage.clear();
    this.sessionStorage.clear();
  };

  resetUserProperties = (): void => {
    Logger.debug("Datamanager : Removing the user attributes from the storage");
    this.storage.remove(KLARITICS_USER_ATTR_KEY);
  };

  persistInstallationTime = (time: number): void => {
    Logger.debug(`DataManager : Setting the installation time to ${time}`);
    this.storage.set(KLARITICS_INSTALL_KEY, String(time));
  };

  getInstallationTime = (defaultTime: number): number => {
    const storedInstallTime = this.storage.get(KLARITICS_INSTALL_KEY);
    if (!isDefined(storedInstallTime)) {
      Logger.debug(
        `Datamanager : Stored installation time not found. Persisting and returning the default time ${defaultTime}`
      );
      this.persistInstallationTime(defaultTime);
      return defaultTime;
    }
    Logger.debug(`Datamanager : Stored installation time ${storedInstallTime}`);
    return Number(storedInstallTime);
  };

  persistOldChunk = (data: Chunk): void => {
    Logger.debug(
      "Datamanager : Persisting the old session chunk to the storage"
    );
    this.storage.set(
      OLD_SESSIONS_DATA,
      encode(this._siteId, JSON.stringify(data)) as string
    );
  };

  getOldChunk = (): Chunk | null => {
    Logger.debug(
      "Datamanager : Getting the old session chunk details from the storage"
    );
    let data: unknown = this.storage.get(OLD_SESSIONS_DATA);
    const jsonData = getJSONOrNull(data);
    if (jsonData === null) {
      try {
        data = JSON.parse(decode(this._siteId, data as string));
      } catch (e) {
        data = null;
      }
    } else {
      data = jsonData;
    }
    return (data as Chunk) ?? null;
  };

  removeOldChunk = (): void => {
    Logger.debug(
      "Datamanager : Removing the old session chunk details from the storage"
    );
    this.storage.remove(OLD_SESSIONS_DATA);
  };

  getOldChunkDetails = (): ChunkDetails | null => {
    return this.getItem(
      KLARITICS_UNSENT_CHUNK_DETAILS,
      true
    ) as ChunkDetails | null;
  };

  persistOldChunkDetails = (data: ChunkDetails): void => {
    Logger.debug(
      "Datamanager : Persisting the old session chunk details to the storage"
    );
    this.storage.set(
      KLARITICS_UNSENT_CHUNK_DETAILS,
      encode(this._siteId, JSON.stringify(data)) as string
    );
  };

  removeOldChunkDetails = (): void => {
    Logger.debug(
      "Datamanager : Removing the old session chunk details from the storage"
    );
    this.storage.remove(KLARITICS_UNSENT_CHUNK_DETAILS);
  };

  getSessionsChunkNumberMap = (): Record<string, number> => {
    Logger.debug("Datamanager : Getting the session chunknumber map");
    let sessionChunkMap = this.getItem(
      KLARITICS_SESSION_CHUNK_NUMBER_MAP,
      true
    ) as Record<string, number> | null;
    if (!isDefined(sessionChunkMap)) sessionChunkMap = {};
    return sessionChunkMap!;
  };

  persistSessionsChunkNumberMap = (
    chunkNumberMap: Record<string, number>
  ): void => {
    Logger.debug("Datamanager : Persisting the new session chunknumber map");
    const encodedMap = encode(this._siteId, JSON.stringify(chunkNumberMap));
    this.persist(KLARITICS_SESSION_CHUNK_NUMBER_MAP, encodedMap as string);
  };

  updateRetainedDayCount(startTime: number): void {
    Logger.debug("Datamanager : Re-calculating and updating the retention day");
    const installationTime = Number(this.getItem(KLARITICS_INSTALL_KEY));
    const hours = Math.abs(installationTime - startTime) / 36e5;
    const retained_days = Math.floor(hours / 24);
    this.persist(KLARITICS_RETAINED_DAYS, String(retained_days));
  }

  getNewChunk = (
    sdkStartTime = Date.now(),
    sessionStartTime = Date.now(),
    newSessionId: string,
    idleTimeout = 3600
  ): { is_new_session: boolean; chunk: Chunk } => {
    Logger.debug("Datamanager : Creating a new chunk");
    let is_new_session = false;
    const lastEventTime =
      Number(this.storage.get(KLARITICS_LAST_EVENT_TIME)) || 0;
    const currentTime = Date.now();
    const lastEventToNowDiff = Math.round(
      millisToSeconds(currentTime - lastEventTime)
    );
    let currentChunk = this.getCurrentChunk();

    if (lastEventToNowDiff > idleTimeout || isNotDefined(currentChunk)) {
      is_new_session = true;
    }

    let chunk: Chunk;
    if (!is_new_session) {
      Logger.debug(
        "Datamanager : This is an old session. Updating the existing chunk"
      );
      const {
        chunk_meta: { number },
        session_info: { id }
      } = currentChunk!;
      const chunkNumber = number + 1;
      chunk = { ...currentChunk! };
      chunk.chunk_meta = {
        ...chunk.chunk_meta,
        number: chunkNumber,
        id: id + ":" + chunkNumber,
        reporting_times: [Date.now()]
      };
      chunk.chunk_details = { ...DEFAULT_CHUNK_DETAILS };
    } else {
      Logger.debug("Datamanager : This is a new session. Creating a new chunk");
      chunk = chunkInitialState(sdkStartTime, sessionStartTime, newSessionId);
    }

    return { is_new_session, chunk };
  };

  clearCurrentChunk = (): void => {
    Logger.debug("Datamanager : Current chunk removed from storage");
    this.storage.remove(KLARITICS_CURRENT_CHUNK);
  };

  persistCurrentChunk = (chunk: Chunk): void => {
    Logger.debug("Datamanager : Persisting the Current chunk to storage");
    ensureSessionInfoKeys(
      chunk as unknown as { session_info?: Record<string, unknown> }
    );
    this.storage.set(
      KLARITICS_CURRENT_CHUNK,
      encode(this._siteId, JSON.stringify(chunk)) as string
    );
  };

  getCurrentChunk = (): Chunk | null => {
    return this.getItem(KLARITICS_CURRENT_CHUNK, true) as Chunk | null;
  };
}
